import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
} from '@saay/shadcn/components/item';
import { cn } from '@saay/shadcn/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { IconTile, TONE_SOFT, type Tone } from './tone.js';

/**
 * One row (a shadcn Item): icon tile, bold title, muted description, and an end slot (amount,
 * badge, action, or a chevron). `tile` is the white row on a grey surface; `plain` sits in a
 * divided list. Pass `render` (a link or a button) when the whole row opens something.
 */
export function ListRow({
  icon,
  tone,
  title,
  description,
  end,
  look = 'tile',
  className,
  ...props
}: Omit<ComponentProps<typeof Item>, 'title'> & {
  readonly icon?: LucideIcon;
  readonly tone?: Tone;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly end?: ReactNode;
  readonly look?: 'tile' | 'plain';
}) {
  return (
    <Item
      className={cn(
        'gap-3 text-start',
        look === 'tile' ? 'rounded-2xl bg-background p-3' : 'rounded-none px-0 py-3.5',
        className,
      )}
      {...props}
    >
      {icon === undefined ? null : (
        <ItemMedia>
          <IconTile icon={icon} size="sm" tone={tone ?? 'forest'} />
        </ItemMedia>
      )}
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="font-bold">{title}</ItemTitle>
        {description === undefined ? null : (
          <ItemDescription className="text-xs">{description}</ItemDescription>
        )}
      </ItemContent>
      {end === undefined ? null : <ItemActions>{end}</ItemActions>}
    </Item>
  );
}

/** A divided list of `plain` rows on one surface. */
export function RowList({
  children,
  className,
}: {
  readonly children: ReactNode[];
  readonly className?: string;
}) {
  return (
    <ItemGroup className={cn('gap-0', className)}>
      {children.map((child, index) => (
        // ItemGroup is a list: each row (with its separator) is one listitem.
        <div key={index} role="listitem">
          {index === 0 ? null : <ItemSeparator className="my-0" />}
          {child}
        </div>
      ))}
    </ItemGroup>
  );
}

/**
 * An attention card: a tinted row with an icon tile, bold title, muted line, and one action (a dark
 * pill `Button`). Danger only for overdue; clay for waiting; gold for notices and offers.
 */
export function Notice({
  icon,
  tone,
  title,
  description,
  action,
  className,
}: {
  readonly icon: LucideIcon;
  readonly tone: Tone;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly className?: string;
}) {
  return (
    <Item className={cn('gap-3 rounded-3xl p-4', TONE_SOFT[tone], 'text-foreground', className)}>
      <ItemMedia>
        <IconTile className="bg-background/70" icon={icon} tone={tone} />
      </ItemMedia>
      <ItemContent className="min-w-0 gap-0.5">
        <ItemTitle className="font-bold text-foreground">{title}</ItemTitle>
        {description === undefined ? null : (
          <ItemDescription className="text-xs">{description}</ItemDescription>
        )}
      </ItemContent>
      {action === undefined ? null : <ItemActions>{action}</ItemActions>}
    </Item>
  );
}
